import * as React from 'react';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { DownloadIcon } from 'lucide-react';
import { ScheduledReport } from 'contracts/scheduled-reports';
import { Checkbox } from 'components/ui/checkbox';
import { DataTableColumnHeader as Header } from 'components/ui/DataTable';
import { Button } from 'components/ui/ButtonGs';
import { cn } from 'lib/utils';

export type BuildSelectionOptions = {
  size?: number;
  headerCheckboxEnabled?: 'when-some-rows-are-selected' | boolean;
  headerCheckboxHint?: string;
};

const SELECTION_COLUMN_ID = 'Selection';
const SELECTION_COLUMN_SIZE = 48;

const CheckboxWrapper = (props: React.ComponentProps<'div'>) => (
  <div
    {...props}
    className={cn(
      'flex w-12 h-12 -mx-4 -my-4 items-center justify-center cursor-default rounded-sm transition-colors hover:bg-slate-100/80',
      props?.className
    )}
    onClick={(e) => {
      e.stopPropagation();
    }}
  />
);

class DataTableHelper<TData = any> {
  protected globalProperties: Partial<ColumnDef<TData>> | undefined;

  public constructor(props?: Partial<ColumnDef<TData>>) {
    this.globalProperties = props;
  }

  public buildColumn<TAccessorKey = keyof TData>(
    props: ColumnDef<TData> & {
      id: string;
      accessorKey?: TAccessorKey;
      title?: React.ReactNode;
      content?: (row: TData) => React.ReactNode;
	  headerClassName?: string;
	  cellClassName?: string;
    },
  ): ColumnDef<TData> {
    const { id, accessorKey, title, content, headerClassName, cellClassName, ...defaultProps } = props;
    return {
      id,
      ...(accessorKey && {
        accessorKey: accessorKey as string,
      }),
      header: ({ column }) => <Header column={column} title={title ?? id} className={headerClassName} />,
      ...(content && {
        cell: ({ row }) => <div className={cellClassName}>{content(row.original)}</div>,
      }),
      ...defaultProps,
      ...this.globalProperties,
    };
  }

  public buildSelectionByRowColumn(props?: {
    size?: number
  }): ColumnDef<TData> {
    return {
      id: SELECTION_COLUMN_ID,
      size: props?.size ?? SELECTION_COLUMN_SIZE,
      cell: ({ row }) => (
        <CheckboxWrapper>
          <Checkbox
            onClick={e => e.stopPropagation()}
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            disabled={!row.getCanSelect()}
            aria-label="Select row"
          />
        </CheckboxWrapper>
      ),
      enableHiding: false,
      enableSorting: false,
      ...props,
    };
  }

  public buildSelectionByPageColumn(props?: BuildSelectionOptions): ColumnDef<TData> {
    return {
      id: SELECTION_COLUMN_ID,
      size: props?.size ?? SELECTION_COLUMN_SIZE,
      header: ({ table }) => {
        const checkboxDisabled =
          props?.headerCheckboxEnabled === false ||
          (props?.headerCheckboxEnabled === 'when-some-rows-are-selected' &&
            !table.getIsSomeRowsSelected());

        return (
          <CheckboxWrapper title={props?.headerCheckboxHint}>
            <Checkbox
              onClick={e => e.stopPropagation()}
              disabled={checkboxDisabled}
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() && 'indeterminate')
              }
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all rows on page"
            />
          </CheckboxWrapper>
        );
      },
      cell: ({ row }) => (
        <CheckboxWrapper>
          <Checkbox
            onClick={e => e.stopPropagation()}
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            disabled={!row.getCanSelect()}
            aria-label="Select row"
          />
        </CheckboxWrapper>
      ),
      enableHiding: false,
      enableSorting: false,
      ...props,
    };
  }

  public buildSelectionByTableColumn(props?: BuildSelectionOptions): ColumnDef<TData> {
    return {
      id: SELECTION_COLUMN_ID,
      size: props?.size ?? SELECTION_COLUMN_SIZE,
      header: ({ table }) => {
        const checkboxDisabled =
          props?.headerCheckboxEnabled === false ||
          (props?.headerCheckboxEnabled === 'when-some-rows-are-selected' &&
            !table.getIsSomeRowsSelected());

        return (
          <CheckboxWrapper title={props?.headerCheckboxHint}>
            <Checkbox
              disabled={checkboxDisabled}
              onClick={e => e.stopPropagation()}
              checked={
                table.getIsAllRowsSelected() ||
                (table.getIsSomeRowsSelected() && 'indeterminate')
              }
              onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
              aria-label="Select all rows"
            />
          </CheckboxWrapper>
        );
      },
      cell: ({ row }) => (
        <CheckboxWrapper>
          <Checkbox
            onClick={e => e.stopPropagation()}
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            disabled={!row.getCanSelect()}
            aria-label="Select row"
          />
        </CheckboxWrapper>
      ),
      enableHiding: false,
      enableSorting: false,
    };
  }
}

class ScheduledReportsDataTableHelper<TData = any> extends DataTableHelper<TData> {
  constructor(props?: Partial<ColumnDef<TData>>) {
    super(props);
  }

  public buildReportStatusColumn<TAccessorKey = keyof TData>(
    props: ColumnDef<TData> & {
      id: string;
      accessorKey?: TAccessorKey;
      title?: React.ReactNode;
      onDownloadButtonClick?: (reportId: number) => void;
    },
  ): ColumnDef<TData> {
    const { id, accessorKey, title, onDownloadButtonClick, ...defaultProps } = props;
    return {
      id,
      ...(accessorKey && {
        accessorKey: accessorKey as string,
      }),
      header: ({ column }) => <Header column={column} className={'text-right'} title={title ?? id} />,
      cell: ({ row }: CellContext<TData, unknown>) => {
        const { status, download_url, id } = row.original as unknown as ScheduledReport;

        const statusText = (() => {
          switch (status) {
            case 'pending':
              return 'Pendente';
            case 'processing':
              return 'Processando';
            case 'finished':
              return 'Finalizado';
            case 'failed':
              return 'Falhou';
            case 'canceled':
              return 'Cancelado';
          }
        })();

        if (status === 'finished' && download_url) {
          return (
            <div className={'flex flex-row justify-end'}>
              <Button.Root
                size={'xs'}
                variant={'primary'}
                className={'pl-2 justify-start'}
                onClick={() => onDownloadButtonClick?.(id)}
              >
                {statusText}
                <Button.Icon>
                  <DownloadIcon className={'w-4 h-4 !fill-current'} />
                </Button.Icon>
              </Button.Root>
            </div>
          );
        }

        return (
          <div className={'pl-2 text-sm flex items-center justify-end'}>{statusText}</div>
        );
      },
      ...defaultProps,
      ...this.globalProperties,
    };
  }
}

export { DataTableHelper, ScheduledReportsDataTableHelper };
